import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useProducts, useUploadMedia } from "@/lib/api/hooks";
import { ArrowLeft, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { MultiImageUpload } from "@/components/ui/multi-image-upload";
import { Product } from "@/lib/api/types";
import { toast } from "react-hot-toast";
import * as api from "@/lib/api/endpoints";

export default function StoreProductEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = id && id !== 'new';

    const { create, update } = useProducts();
    const uploadMedia = useUploadMedia();

    const [isLoading, setIsLoading] = useState(isEditMode);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    // Stock as it was when the form loaded. Saving sends stock only if it was changed, together
    // with this value, so sales made while the form is open aren't overwritten (ORD-06).
    const [loadedStock, setLoadedStock] = useState<number | null>(null);
    const [stockChange, setStockChange] = useState("");
    const [isAdjusting, setIsAdjusting] = useState(false);

    const handleAdjustStock = async () => {
        const change = parseInt(stockChange, 10);
        if (!id || !Number.isInteger(change) || change === 0) {
            toast.error("Enter a whole number, e.g. 10 to add or -2 to remove");
            return;
        }
        try {
            setIsAdjusting(true);
            const { stockQuantity } = await api.products.adjustStock(id, change);
            setLoadedStock(stockQuantity);
            setFormData(prev => ({ ...prev, stockQuantity }));
            setStockChange("");
            toast.success(`Stock is now ${stockQuantity}`);
        } catch {
            // Reason shown by the API error toast
        } finally {
            setIsAdjusting(false);
        }
    };

    const [formData, setFormData] = useState<Partial<Product>>({
        name: "",
        description: "",
        price: 0,
        stockQuantity: 0,
        category: "",
        imageUrl: "",
        isActive: true,
        featured: false,
        images: [],
        sizes: [],
        colors: []
    });

    // Fetch product if edit mode
    useEffect(() => {
        const fetchProduct = async () => {
            if (!isEditMode || !id) return;

            try {
                setIsLoading(true);
                const product = await api.products.get(id);

                setFormData({
                    ...product,
                    price: product.price / 100 // Convert cents to KES for display
                });
                setLoadedStock(product.stockQuantity ?? 0);
            } catch (error) {
                toast.error("Failed to load product");
                navigate("/store/products");
            } finally {
                setIsLoading(false);
            }
        };

        fetchProduct();
    }, [id, isEditMode, navigate]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: parseFloat(value) || 0
        }));
    };

    const handleToggle = (name: keyof Product) => {
        setFormData(prev => ({
            ...prev,
            [name]: !prev[name]
        }));
    };

    const handleMultiImageUpload = async (files: File[]) => {
        if (files.length === 0) return;

        try {
            setIsUploading(true);
            const uploadPromises = files.map(file => uploadMedia.mutateAsync(file));
            const results = await Promise.all(uploadPromises);
            const newUrls = results.map(r => r.url);

            setFormData(prev => ({
                ...prev,
                images: [...(prev.images || []), ...newUrls],
                // Set first image as main imageUrl if not set
                imageUrl: prev.imageUrl || newUrls[0]
            }));

            toast.success(`${files.length} image(s) uploaded successfully`);
        } catch (error) {
            toast.error("Failed to upload images");
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveImage = (index: number) => {
        setFormData(prev => {
            const newImages = [...(prev.images || [])];
            newImages.splice(index, 1);
            return {
                ...prev,
                images: newImages,
                // Update main imageUrl if it was removed
                imageUrl: prev.imageUrl === prev.images?.[index] ? newImages[0] || "" : prev.imageUrl
            };
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.price) {
            toast.error("Name and Price are required");
            return;
        }

        try {
            setIsSaving(true);

            if (isEditMode && id) {
                const { stockQuantity, ...rest } = formData;
                const stockChanged = loadedStock !== null && Number(stockQuantity) !== loadedStock;
                await update({
                    id,
                    data: stockChanged ? { ...rest, stockQuantity, expectedStockQuantity: loadedStock } : rest,
                });
            } else {
                await create(formData);
            }

            navigate("/store/products");
        } catch (error: any) {
            // Orders came in while editing: the server saved nothing and says what stock is now.
            // Show it so the editor can decide again (the error message is already shown).
            if (error?.response?.status === 409 && typeof error.response.data?.currentStock === 'number') {
                const current = error.response.data.currentStock;
                setLoadedStock(current);
                setFormData(prev => ({ ...prev, stockQuantity: current }));
            }
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-12">
            {/* Header with back button */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate("/store/products")}>
                    <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex-1">
                    <PageHeader
                        title={isEditMode ? "Edit Product" : "New Product"}
                        description={isEditMode ? "Update product details and inventory" : "Add a new product to your store"}
                    />
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-6 lg:grid-cols-3">

                    {/* Main Info */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-card border rounded-lg p-6 space-y-4">
                            <h3 className="font-semibold">Basic Information</h3>

                            <div className="grid gap-2">
                                <Label htmlFor="name">Product Name *</Label>
                                <Input
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="e.g. Elimisha T-Shirt"
                                    required
                                />
                            </div>

                            <div className="grid gap-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Describe the product..."
                                    rows={4}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="price">Price (KES) *</Label>
                                    <Input
                                        id="price"
                                        name="price"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={formData.price}
                                        onChange={handleNumberChange}
                                        placeholder="0.00"
                                        required
                                    />
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="stockQuantity">Stock Quantity</Label>
                                    {isEditMode && (
                                        <>
                                            <p className="text-xs text-muted-foreground">
                                                Live stock when this page opened: {loadedStock ?? 0}. Orders reduce it automatically.
                                                To restock, use Add / remove below.
                                            </p>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="number"
                                                    step="1"
                                                    value={stockChange}
                                                    onChange={(e) => setStockChange(e.target.value)}
                                                    placeholder="+10 or -2"
                                                    aria-label="Stock change"
                                                />
                                                <Button type="button" variant="outline" onClick={handleAdjustStock} disabled={isAdjusting || !stockChange}>
                                                    {isAdjusting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add / remove'}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                    <Input
                                        id="stockQuantity"
                                        name="stockQuantity"
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={formData.stockQuantity}
                                        onChange={handleNumberChange}
                                        placeholder="0"
                                    />
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <Label htmlFor="category">Category</Label>
                                <Input
                                    id="category"
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    placeholder="e.g. Apparel, Accessories"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        <div className="bg-card border rounded-lg p-6 space-y-4">
                            <h3 className="font-semibold">Product Images</h3>
                            <MultiImageUpload
                                value={formData.images || []}
                                onChange={handleMultiImageUpload}
                                onRemove={handleRemoveImage}
                                maxImages={5}
                                disabled={isUploading}
                            />
                            {isUploading && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Uploading images...
                                </div>
                            )}
                        </div>

                        <div className="bg-card border rounded-lg p-6 space-y-4">
                            <h3 className="font-semibold">Settings</h3>

                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label htmlFor="isActive">Active Status</Label>
                                    <p className="text-xs text-muted-foreground">Visible in store</p>
                                </div>
                                <Switch
                                    id="isActive"
                                    checked={formData.isActive}
                                    onCheckedChange={() => handleToggle('isActive')}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label htmlFor="featured">Featured Product</Label>
                                    <p className="text-xs text-muted-foreground">Show in featured section</p>
                                </div>
                                <Switch
                                    id="featured"
                                    checked={formData.featured}
                                    onCheckedChange={() => handleToggle('featured')}
                                />
                            </div>
                        </div>
                    </div>

                </div>

                <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => navigate("/store/products")}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSaving || isUploading}>
                        {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {isEditMode ? "Save Changes" : "Create Product"}
                    </Button>
                </div>
            </form>
        </div>
    );
}
